/**
 * Word Edit Operations Service (DEPRECATED)
 *
 * @deprecated This file uses the old searchParagraph approach which can exceed
 * the 255-character limit on Word Desktop. Use wordBatchEditOperations.ts instead,
 * which implements document-wide search with searchPhraseOccurrence targeting.
 *
 * **Migration Path:**
 * - Replace `applyEditToDocument()` with `applySingleEdit()` from wordBatchEditOperations.ts
 * - Replace `applyAutoRedlineEdits()` with `applyEditsSequentially()` from wordSequentialEditService.ts
 *
 * **Why Deprecated:**
 * - Uses searchParagraph parameter (often >255 chars) which fails on Word Desktop
 * - Performs 2-phase search (paragraph → phrase) instead of direct document-wide search
 * - Not compatible with backend's new searchPhraseOccurrence field
 *
 * **Scheduled for Removal:** TBD (after confirming no usage in production)
 */

/* global Word */

import {
  EditOperation,
  EditApplicationResult,
  AutoRedlineOutput,
} from "../../../interfaces/autoRedlineTypes";
import { createLogger } from "../../../utils/logger";

const log = createLogger("WordEditOperations");

/**
 * Apply a single edit operation to the Word document
 *
 * @deprecated Use `applySingleEdit()` from wordBatchEditOperations.ts instead.
 * This function requires searchParagraph which can exceed 255 chars on Word Desktop.
 *
 * @param edit - The edit operation to apply
 * @param searchParagraph - The paragraph text to search (DEPRECATED: often >255 chars)
 * @returns Promise of edit application result
 */
export async function applyEditToDocument(
  edit: EditOperation,
  searchParagraph: string
): Promise<EditApplicationResult> {
  const startTime = Date.now();

  try {
    return await Word.run(async (context) => {
      // Step 1: Find the searchParagraph text chunk in the document
      // This is NOT a Word.Paragraph object, but a text chunk that might span multiple paragraphs
      const searchParagraphResults = context.document.body.search(searchParagraph, {
        matchCase: false, // Case-insensitive for flexibility
        matchWholeWord: false, // It's a text chunk, not individual words
        ignorePunct: true, // Lenient on punctuation differences
        ignoreSpace: true, // Lenient on spacing differences
      });

      searchParagraphResults.load("items");
      await context.sync();

      if (searchParagraphResults.items.length === 0) {
        log.error("searchParagraph text chunk not found in document", {
          editId: edit.editId,
          searchParagraphPreview: searchParagraph.substring(0, 100) + "...",
          searchParagraphLength: searchParagraph.length,
        });
        return {
          success: false,
          editId: edit.editId,
          error: `searchParagraph not found in document`,
          duration: Date.now() - startTime,
        };
      }

      // Get the first match (there should only be one)
      const paragraphRange = searchParagraphResults.items[0];

      // Step 2: Search for the searchPhrase within the found range
      let searchPhraseResults = paragraphRange.search(edit.searchPhrase, {
        matchCase: false, // Case-insensitive
        matchWholeWord: false, // Always false for phrases
        ignorePunct: true, // Lenient on punctuation
        ignoreSpace: false, // Keep exact spacing for phrases
      });

      searchPhraseResults.load("items");
      await context.sync();

      log.debug("Search for phrase within paragraph range", {
        editId: edit.editId,
        searchPhrase: edit.searchPhrase,
        resultCount: searchPhraseResults.items.length,
        targetOccurrence: edit.targetOccurrence,
      });

      // Fallback: If no results with lenient search, try stricter
      if (searchPhraseResults.items.length === 0) {
        log.debug("No results with lenient search, trying stricter search", {
          editId: edit.editId,
          searchPhrase: edit.searchPhrase,
        });

        searchPhraseResults = paragraphRange.search(edit.searchPhrase, {
          matchCase: false,
          matchWholeWord: false, // Always false for phrases
          ignorePunct: false, // Exact punctuation
          ignoreSpace: false, // Exact spacing
        });

        searchPhraseResults.load("items");
        await context.sync();

        if (searchPhraseResults.items.length > 0) {
          log.debug("✓ Found with strict search", {
            editId: edit.editId,
            resultCount: searchPhraseResults.items.length,
          });
        }
      }

      // Check if we found the search phrase
      if (searchPhraseResults.items.length === 0) {
        return {
          success: false,
          editId: edit.editId,
          error: `Search phrase not found within searchParagraph: "${edit.searchPhrase}"`,
          duration: Date.now() - startTime,
        };
      }

      // Always use the first occurrence of searchPhrase
      // targetOccurrence should be used for targetText within searchPhrase, not for searchPhrase itself
      const searchPhraseRange = searchPhraseResults.items[0];

      // Step 3: Apply edit based on position and targetText
      switch (edit.position) {
        case "replace":
          if (edit.targetText) {
            // Find targetText within the searchPhrase range
            // targetText might be punctuation like "," so we need exact matching
            const targetSearch = searchPhraseRange.search(edit.targetText, {
              matchCase: false,
              matchWholeWord: edit.matchWholeWord ?? false, // Could be punctuation
              ignorePunct: false, // Exact punctuation matching
              ignoreSpace: false, // Exact spacing
            });
            targetSearch.load("items");
            await context.sync();

            // Use targetOccurrence to select which occurrence of targetText to replace
            const targetOccurrence = edit.targetOccurrence ?? 1;
            const targetIndex = targetOccurrence - 1;

            if (targetSearch.items.length === 0) {
              return {
                success: false,
                editId: edit.editId,
                error: `Target text "${edit.targetText}" not found within search phrase`,
                duration: Date.now() - startTime,
              };
            } else if (targetIndex >= targetSearch.items.length) {
              return {
                success: false,
                editId: edit.editId,
                error: `Target text "${edit.targetText}" occurrence ${targetOccurrence} not found (only ${targetSearch.items.length} occurrences found)`,
                duration: Date.now() - startTime,
              };
            } else {
              // Replace the specified occurrence of targetText
              // Use 2-step approach for consistent behavior across Desktop and Online
              const targetRange = targetSearch.items[targetIndex];
              // Step 1: Insert replacement text after the target
              targetRange.insertText(edit.replacementText, Word.InsertLocation.after);
              // Step 2: Delete the original target text
              targetRange.insertText("", Word.InsertLocation.replace);
            }
          } else {
            // Replace entire searchPhrase
            // Use 2-step approach for consistent behavior across Desktop and Online
            // Step 1: Insert replacement text after the searchPhrase
            searchPhraseRange.insertText(edit.replacementText, Word.InsertLocation.after);
            // Step 2: Delete the original searchPhrase
            searchPhraseRange.insertText("", Word.InsertLocation.replace);
          }
          break;

        case "before":
          searchPhraseRange.insertText(edit.replacementText, Word.InsertLocation.start);
          break;

        case "after":
          searchPhraseRange.insertText(edit.replacementText, Word.InsertLocation.end);
          break;

        default:
          return {
            success: false,
            editId: edit.editId,
            error: `Unknown position: ${edit.position}`,
            duration: Date.now() - startTime,
          };
      }

      await context.sync();

      log.info("Successfully applied edit", {
        editId: edit.editId,
        position: edit.position,
        duration: Date.now() - startTime,
      });

      return {
        success: true,
        editId: edit.editId,
        duration: Date.now() - startTime,
      };
    });
  } catch (error) {
    log.error("Failed to apply edit", {
      editId: edit.editId,
      error: error instanceof Error ? error.message : String(error),
    });

    return {
      success: false,
      editId: edit.editId,
      error: error instanceof Error ? error.message : "Unknown error occurred",
      duration: Date.now() - startTime,
    };
  }
}

/**
 * Apply multiple edits from an AutoRedlineOutput
 *
 * @deprecated Use `applyEditsSequentially()` from wordSequentialEditService.ts instead.
 * This function uses searchParagraph which can fail on Word Desktop (255-char limit).
 *
 * @param autoRedline - The AutoRedlineOutput containing edits
 * @param editIds - Optional array of specific edit IDs to apply
 * @returns Promise of edit application results
 */
export async function applyAutoRedlineEdits(
  autoRedline: AutoRedlineOutput,
  editIds?: string[]
): Promise<EditApplicationResult[]> {
  const results: EditApplicationResult[] = [];

  // Filter edits if specific IDs provided
  let editsToApply = editIds
    ? autoRedline.edits.filter((e) => editIds.includes(e.editId))
    : autoRedline.edits;

  // IMPORTANT: Apply edits in reverse order to maintain position integrity
  // When applying from the beginning, each edit shifts text positions
  editsToApply = [...editsToApply].reverse();

  log.info("Applying auto-redline edits in reverse order", {
    sectionName: autoRedline.sectionName,
    totalEdits: autoRedline.edits.length,
    editsToApply: editsToApply.length,
    order: editsToApply.map((e) => e.editId),
  });

  // Apply edits sequentially (in reverse order)
  for (const edit of editsToApply) {
    const result = await applyEditToDocument(edit, autoRedline.searchParagraph);
    results.push(result);

    // Optional: Add small delay between edits for better UX
    if (editsToApply.indexOf(edit) < editsToApply.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
  }

  // Reverse results back to match original order
  return results.reverse();
}
